/**
 * Public example runs for /example — the "not a black box" page.
 *
 * WHY THIS EXISTS: two independent strangers on the launch post (DL, Eric Choi)
 * said the same thing — the product's observability is real but locked behind
 * signup, so a prospective buyer can't see what a run does or costs before
 * committing. This page shows one complete run, and one honest failure, with no
 * account required.
 *
 * The data is REPRESENTATIVE, not a real user's run (that would leak their
 * input/output — this page is public). But the cost numbers are REAL: computed
 * with the same services/llm/pricing.py the product uses. Do not round them to
 * look nicer — the whole point is that the number is honest, including the
 * fractions of a cent.
 *
 * Cost math (verified 2026-07-24, claude-sonnet-4-6):
 *   Triage Agent   800 tok  → 0.4560c
 *   Reply Drafter 1650 tok  → 0.9870c   total 1.4430c  ($0.0144)
 *   Failed run     430 tok  → 0.2370c   (charged for tokens spent before the error)
 */

export interface ExampleStep {
  n: number;
  agent: string;
  status: 'COMPLETED' | 'FAILED';
  durationMs: number;
  tokens: number;
  costCents: number;
  /** What the agent did this step — the "action", not just the text. */
  action: string;
  output: string;
  /** Present only on the failed step. */
  error?: string;
}

export interface ExampleRun {
  id: string;
  title: string;
  status: 'COMPLETED' | 'FAILED';
  workflowType: string;
  durationMs: number;
  totalTokens: number;
  totalCostCents: number;
  input: string;
  steps: ExampleStep[];
  /** Final answer for a completed run; null for a failed one. */
  finalOutput: string | null;
}

export const COMPLETED_RUN: ExampleRun = {
  id: 'exec_9f2a…demo-complete',
  title: 'Customer support triage → draft reply',
  status: 'COMPLETED',
  workflowType: 'sequential',
  durationMs: 8420,
  totalTokens: 2450,
  totalCostCents: 1.443,
  input:
    'Subject: Still charged after cancelling\n\n"I cancelled my plan on the 3rd but I was charged again on ' +
    'the 5th. Order #48213. Can you refund it? This is the second time."',
  steps: [
    {
      n: 1,
      agent: 'Triage Agent',
      status: 'COMPLETED',
      durationMs: 3120,
      tokens: 800,
      costCents: 0.456,
      action:
        'Read the message, classified it, and looked up the order via the http_request tool ' +
        '(GET /orders/48213 → 200).',
      output:
        '{\n  "category": "billing",\n  "intent": "refund_request",\n  "sentiment": "frustrated",\n' +
        '  "order_found": true,\n  "charge_after_cancel": true,\n  "priority": "high"\n}',
    },
    {
      n: 2,
      agent: 'Reply Drafter',
      status: 'COMPLETED',
      durationMs: 5300,
      tokens: 1650,
      costCents: 0.987,
      action:
        'Took the structured handoff from step 1 and drafted a reply. Did NOT issue the refund — ' +
        'that step is gated for human approval.',
      output:
        'Hi — you\'re right, and I\'m sorry. Order #48213 was cancelled on the 3rd but a charge went ' +
        'through on the 5th. I\'ve flagged it for a refund of the duplicate charge; you\'ll see it back ' +
        'on your card in 5–10 days. Because this is the second time, I\'ve also added a note so it ' +
        'doesn\'t recur. — Support',
    },
  ],
  finalOutput:
    'A ready-to-send reply, plus a refund flagged for one-click human approval. The agent decided ' +
    'the category, looked up the real order, and stopped short of moving money on its own.',
};

export const FAILED_RUN: ExampleRun = {
  id: 'exec_3c7b…demo-failed',
  title: 'Inventory check — the API was down',
  status: 'FAILED',
  workflowType: 'sequential',
  durationMs: 4110,
  totalTokens: 430,
  totalCostCents: 0.237,
  input: '"How many units of SKU-7741 are in the Berlin warehouse right now?"',
  steps: [
    {
      n: 1,
      agent: 'Inventory Agent',
      status: 'FAILED',
      durationMs: 4110,
      tokens: 430,
      costCents: 0.237,
      action:
        'Decided to call the inventory API via http_request (GET /stock/SKU-7741). The API returned ' +
        '503 Service Unavailable. The agent retried once, got 503 again, and stopped.',
      output: '',
      error:
        'ToolError: http_request → 503 Service Unavailable after 2 attempts (upstream: ' +
        'inventory.internal). Run marked FAILED; no partial answer invented.',
    },
  ],
  finalOutput: null,
};
